import React, { useState } from 'react';
import { Search, Download, List, Film, AlertCircle, X } from 'lucide-react';
import { searchLists, getListDetails, convertTMDBListToMovies } from '../../services/tmdbService';

const TMDBListImporter = ({ isVisible, onClose, onImportMovies }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [listDetails, setListDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const results = await searchLists(searchQuery);
      setSearchResults(results);
      
      if (results.length === 0) {
        setError('No lists found for your search query.');
      }
    } catch (err) {
      setError('Failed to search lists. Please check your TMDB API key.');
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleListSelect = async (list) => {
    setSelectedList(list);
    setLoading(true);
    setError('');
    
    try {
      const details = await getListDetails(list.id);
      setListDetails(details);
    } catch (err) {
      setError('Failed to load list details.');
      console.error('List details error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!listDetails) return;
    
    setImporting(true);
    setError('');
    
    try {
      const movies = convertTMDBListToMovies(listDetails);
      
      if (movies.length === 0) {
        setError('No movies found in this list.');
        return;
      }
      
      onImportMovies(movies);
      
      // Reset state and close
      setSearchQuery('');
      setSearchResults([]);
      setSelectedList(null);
      setListDetails(null);
      onClose();
    } catch (err) {
      setError('Failed to import movies from list.');
      console.error('Import error:', err);
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedList(null);
    setListDetails(null);
    setError('');
    onClose();
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border-2 border-orange-600 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-900 to-orange-900 px-6 py-4 border-b-2 border-orange-500">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-orange-100 flex items-center gap-2">
              <List size={20} />
              Import Your TMDB Lists
            </h2>
            <button
              onClick={handleClose}
              className="text-orange-300 hover:text-orange-100 transition-colors"
            >
              <X size={24} />
            </button>
          </div>
          <p className="text-orange-200 text-sm mt-1">
            Import your personal movie lists from your TMDB account
          </p>
        </div>

        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          {/* Search Section */}
          <div className="mb-6">
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search your TMDB lists (e.g., 'Horror Collection', 'Watchlist')"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="w-full px-4 py-2 bg-gray-700 border border-orange-500 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-orange-400"
                />
              </div>
              <button
                onClick={handleSearch}
                disabled={loading || !searchQuery.trim()}
                className="bg-orange-600 hover:bg-orange-500 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-bold transition-colors flex items-center gap-2"
              >
                <Search size={16} />
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-900 border border-red-600 rounded-lg flex items-center gap-2">
              <AlertCircle size={20} className="text-red-400 flex-shrink-0" />
              <span className="text-red-200">{error}</span>
            </div>
          )}

          {/* Search Results */}
          {searchResults.length > 0 && !selectedList && (
            <div className="mb-6">
              <h3 className="text-lg font-bold text-orange-300 mb-3">Search Results</h3>
              <div className="space-y-3 max-h-60 overflow-y-auto">
                {searchResults.map((list) => (
                  <div
                    key={list.id}
                    onClick={() => handleListSelect(list)}
                    className="bg-gray-700 border border-orange-500 rounded-lg p-4 cursor-pointer hover:border-orange-400 hover:bg-gray-600 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-bold text-orange-100">{list.name}</h4>
                        <p className="text-gray-300 text-sm mt-1">{list.description || 'No description available'}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-400">
                          <span>📽️ {list.item_count} movies</span>
                          <span>👤 {list.created_by?.username || 'Unknown'}</span>
                        </div>
                      </div>
                      <div className="text-orange-400">
                        <Film size={20} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Selected List Details */}
          {selectedList && listDetails && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-orange-300">List Details</h3>
                <button
                  onClick={() => {
                    setSelectedList(null);
                    setListDetails(null);
                  }}
                  className="text-orange-400 hover:text-orange-300 text-sm"
                >
                  ← Back to Results
                </button>
              </div>
              
              <div className="bg-gray-700 border border-orange-500 rounded-lg p-6">
                <h4 className="text-xl font-bold text-orange-100 mb-2">{listDetails.name}</h4>
                <p className="text-gray-300 mb-4">{listDetails.description || 'No description available'}</p>
                
                <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                  <div>
                    <span className="text-orange-400 font-semibold">Movies:</span>
                    <span className="text-gray-200 ml-2">{listDetails.item_count}</span>
                  </div>
                  <div>
                    <span className="text-orange-400 font-semibold">Created by:</span>
                    <span className="text-gray-200 ml-2">{listDetails.created_by?.username || 'Unknown'}</span>
                  </div>
                </div>

                {/* Movie Preview */}
                {listDetails.items && listDetails.items.length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-orange-300 font-semibold mb-2">Preview (First 5 movies):</h5>
                    <div className="space-y-2">
                      {listDetails.items.slice(0, 5).map((movie) => (
                        <div key={movie.id} className="flex items-center gap-3 text-sm">
                          <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                          <span className="text-gray-200">{movie.title}</span>
                          {movie.release_date && (
                            <span className="text-gray-400">({new Date(movie.release_date).getFullYear()})</span>
                          )}
                        </div>
                      ))}
                      {listDetails.items.length > 5 && (
                        <div className="text-gray-400 text-sm">
                          ... and {listDetails.items.length - 5} more movies
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <button
                  onClick={handleImport}
                  disabled={importing}
                  className="bg-green-600 hover:bg-green-500 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-bold transition-colors flex items-center gap-2"
                >
                  <Download size={16} />
                  {importing ? 'Importing...' : `Import ${listDetails.item_count} Movies`}
                </button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-8">
              <div className="text-orange-400 mb-2">
                <Film className="w-8 h-8 mx-auto animate-pulse" />
              </div>
              <p className="text-gray-300">Loading...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && searchResults.length === 0 && !error && searchQuery && (
            <div className="text-center py-8">
              <div className="text-gray-500 mb-2">
                <Search className="w-8 h-8 mx-auto" />
              </div>
              <p className="text-gray-400">No lists found. Try a different search term.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-700 px-6 py-4 border-t border-orange-500 flex justify-end">
          <button
            onClick={handleClose}
            className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg font-bold transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TMDBListImporter;
